import { useMemo, useState } from "react";
import { Controller, FormProvider, useForm, useFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { applicationSchema, FormValues, Track } from "./lib/schema";
import {
  COUNTRIES,
  ENGAGEMENT_WAYS,
  EXPERIENCE_TYPES,
  GENDER_OPTIONS,
  GUIDANCE_TYPES,
  HEAR_ABOUT_US,
  MARKETS,
  MENTEE_PREFERENCES,
  MENTOR_FREQUENCY,
  MENTOR_HOURS,
  MENTOR_MODELS,
  NON_TECHNICAL_SUBJECTS,
  PAID_OR_VOLUNTEER,
  STUDY_LEVELS,
  SUPPORT_AREA,
  SUPPORT_MODES,
  TECHNICAL_SPECIALIZATIONS,
  YEARS_OF_EXPERIENCE,
  YES_NO,
} from "./data/options";
import { ChipGroup, Field, StarRating } from "./components/Field";
import { FileDrop } from "./components/FileDrop";
import { ProgressBar } from "./components/ProgressBar";
import { Footer, SectionHeading, Shell, SuccessPage, WizardHeader } from "./components/Chrome";

type StepKey =
  | "about"
  | "experience"
  | "support"
  | "engagement"
  | "mentorship"
  | "upload"
  | "review";

const STEP_LABELS: Record<StepKey, string> = {
  about: "About you",
  experience: "Experience",
  support: "Support",
  engagement: "Engagement",
  mentorship: "Mentorship",
  upload: "CV upload",
  review: "Review",
};

function stepsForTrack(track: Track): StepKey[] {
  if (track === "advisor")
    return ["about", "experience", "support", "engagement", "upload", "review"];
  if (track === "mentor") return ["about", "mentorship", "upload", "review"];
  return ["about", "experience", "support", "engagement", "mentorship", "upload", "review"];
}

function fieldsByStep(track: Track): Record<StepKey, (keyof FormValues)[]> {
  return {
    about: ["fullName", "gender", "country", "email", "whatsapp", "linkedin"],
    experience: [
      "ratingTechIndustry",
      "ratingPalestinianEcosystem",
      "experienceTypes",
      "experienceTypeDetails",
      "cLevelExperience",
      "cLevelDetails",
      "currentPosition",
      "currentEmployer",
      "yearsOfExperience",
    ],
    support: [
      "supportArea",
      "nonTechSubjects",
      "nonTechSubjectsOther",
      "techSpecializations",
      "techSpecializationsOther",
      "workedWithGsgBefore",
      "paidOrVolunteer",
      "hourlyRateUsd",
      "markets",
      "marketsExperience",
    ],
    engagement: ["engagementWays", "supportModes", "howHeard", "howHeardOther"],
    mentorship: [
      "isUniversityStudent",
      "studying",
      "studyLevel",
      "hoursPerMonth",
      "meetingFrequency",
      "mentorshipModel",
      "menteePreference",
      "mentoredBefore",
      "guidanceTypes",
      "guidanceOther",
      "technicalExpertise",
      "feedbackSession",
    ],
    upload: ["cv"],
    review:
      track === "mentor"
        ? ["howHeard", "howHeardOther", "anythingElse", "newsletterOptIn"]
        : ["anythingElse", "newsletterOptIn"],
  };
}

const FIELD_LABELS: Partial<Record<keyof FormValues, string>> = {
  fullName: "Full name",
  gender: "Gender",
  country: "Country",
  email: "Email",
  whatsapp: "WhatsApp number",
  linkedin: "LinkedIn URL",
  ratingTechIndustry: "Tech-industry rating",
  ratingPalestinianEcosystem: "Palestinian-ecosystem rating",
  experienceTypes: "Experience types",
  experienceTypeDetails: "Experience details",
  cLevelExperience: "C-level experience (Yes / No)",
  cLevelDetails: "C-level details",
  currentPosition: "Current position",
  currentEmployer: "Current employer",
  yearsOfExperience: "Years of experience",
  supportArea: "Support area (Technical / Non-Technical)",
  nonTechSubjects: "Non-technical subjects",
  techSpecializations: "Technical specializations",
  workedWithGsgBefore: "Worked with GSG before",
  paidOrVolunteer: "Paid or volunteering",
  hourlyRateUsd: "Hourly rate",
  markets: "Markets",
  marketsExperience: "Markets experience description",
  engagementWays: "Engagement ways",
  supportModes: "Support modes",
  isUniversityStudent: "University student (Yes / No)",
  studying: "What you are studying",
  studyLevel: "Study level",
  hoursPerMonth: "Hours per month",
  meetingFrequency: "Meeting frequency",
  mentorshipModel: "Mentorship model",
  menteePreference: "Mentee preference",
  mentoredBefore: "Mentored before (Yes / No)",
  guidanceTypes: "Type of guidance",
  guidanceOther: "Other guidance details",
  technicalExpertise: "Technical area(s) of expertise",
  feedbackSession: "Feedback session (Yes / No)",
  howHeard: "How you heard about us",
  cv: "CV upload",
  anythingElse: "Comments",
  newsletterOptIn: "Newsletter sign-up choice",
};

export function Wizard({ track }: { track: Track }) {
  const STEPS = useMemo(() => stepsForTrack(track), [track]);
  const FIELDS = useMemo(() => fieldsByStep(track), [track]);

  const methods = useForm<FormValues>({
    resolver: zodResolver(applicationSchema),
    mode: "onTouched",
    defaultValues: {
      track,
      // Consent is collected on the hero before the wizard opens.
      safeguardingAgree: true,
      experienceTypes: [],
      supportArea: [],
      nonTechSubjects: [],
      techSpecializations: [],
      markets: [],
      engagementWays: [],
      supportModes: [],
      guidanceTypes: [],
      anythingElse: "",
      cLevelDetails: "",
      experienceTypeDetails: "",
      hourlyRateUsd: "",
      marketsExperience: "",
      howHeardOther: "",
      nonTechSubjectsOther: "",
      techSpecializationsOther: "",
      studying: "",
      guidanceOther: "",
      technicalExpertise: "",
    } as Partial<FormValues> as FormValues,
  });
  const [stepIdx, setStepIdx] = useState(0);
  const [submitState, setSubmitState] = useState<
    { status: "idle" | "submitting" } | { status: "success"; id: string } | { status: "error"; message: string }
  >({ status: "idle" });
  const [validationIssues, setValidationIssues] = useState<
    { step: StepKey | undefined; field: keyof FormValues; label: string; message: string }[]
  >([]);

  const step = STEPS[stepIdx];

  function stepForField(field: keyof FormValues): StepKey | undefined {
    for (const key of STEPS) {
      if (FIELDS[key].includes(field)) return key;
    }
    return undefined;
  }

  async function next() {
    const ok = await methods.trigger(FIELDS[step], { shouldFocus: true });
    if (!ok) return;
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function back() {
    setStepIdx((i) => Math.max(i - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function onSubmit(values: FormValues) {
    setValidationIssues([]);
    setSubmitState({ status: "submitting" });
    try {
      const res = await fetch("/api/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Server returned ${res.status}`);
      }
      const json = (await res.json()) as { id?: string };
      setSubmitState({ status: "success", id: json.id ?? "" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSubmitState({
        status: "error",
        message:
          err instanceof Error
            ? err.message
            : "Submission failed. Please try again or email us directly.",
      });
    }
  }

  function onInvalid(errors: Record<string, { message?: string }>) {
    const issues = Object.entries(errors).flatMap(([key, value]) => {
      const field = key as keyof FormValues;
      const label = FIELD_LABELS[field] ?? field;
      const message =
        (value && typeof value === "object" && "message" in value && value.message) ||
        "Required";
      return [{ step: stepForField(field), field, label, message: String(message) }];
    });
    setValidationIssues(issues);
    setSubmitState({ status: "idle" });
    const firstStepWithError = STEPS.findIndex((s) =>
      issues.some((iss) => iss.step === s),
    );
    if (firstStepWithError >= 0 && firstStepWithError !== stepIdx) {
      setStepIdx(firstStepWithError);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function jumpToStep(key: StepKey | undefined) {
    if (!key) return;
    const idx = STEPS.indexOf(key);
    if (idx >= 0) setStepIdx(idx);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (submitState.status === "success") {
    return <SuccessPage track={track} />;
  }

  return (
    <FormProvider {...methods}>
      <Shell>
        <WizardHeader track={track} />
        <div className="mt-6">
          <ProgressBar
            steps={STEPS.map((k) => ({ key: k, label: STEP_LABELS[k] }))}
            current={stepIdx}
          />
        </div>
        <form
          onSubmit={methods.handleSubmit(onSubmit, onInvalid)}
          className="mt-6 rounded-xl2 bg-white p-6 md:p-8 shadow-card border border-brand-line"
          noValidate
        >
          {validationIssues.length > 0 && (
            <div className="mb-6 rounded-xl2 border border-brand-red/40 bg-brand-red/5 p-4">
              <div className="font-semibold text-brand-red text-[14px]">
                Please fix the following before submitting:
              </div>
              <ul className="mt-2 space-y-1.5 text-[13.5px]">
                {validationIssues.map((iss) => (
                  <li key={String(iss.field)} className="flex items-start gap-2">
                    <span className="text-brand-red mt-0.5">•</span>
                    <button
                      type="button"
                      onClick={() => jumpToStep(iss.step)}
                      className="text-left text-brand-ink hover:underline"
                    >
                      <span className="font-semibold">{iss.label}:</span>{" "}
                      <span className="text-brand-mute">{iss.message}</span>
                      {iss.step && iss.step !== step && (
                        <span className="ml-2 text-brand-teal text-[12px] font-semibold">
                          (go to step)
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {step === "about" && <AboutStep />}
          {step === "experience" && <ExperienceStep />}
          {step === "support" && <SupportStep />}
          {step === "engagement" && <EngagementStep />}
          {step === "mentorship" && <MentorshipStep />}
          {step === "upload" && <UploadStep track={track} />}
          {step === "review" && (
            <ReviewStep track={track} steps={STEPS} onJump={jumpToStep} />
          )}

          {submitState.status === "error" && (
            <div className="mt-6 rounded-xl2 border border-brand-red/40 bg-brand-red/5 p-4 text-[14px] text-brand-red">
              {submitState.message}
            </div>
          )}

          <div className="mt-8 flex flex-col-reverse md:flex-row items-stretch md:items-center justify-between gap-3 pt-6 border-t border-brand-line">
            <button
              type="button"
              onClick={back}
              disabled={stepIdx === 0}
              className="ac-btn-ghost disabled:opacity-50"
            >
              Back
            </button>
            {stepIdx < STEPS.length - 1 ? (
              <button type="button" onClick={next} className="ac-btn-primary">
                Continue
              </button>
            ) : (
              <button
                type="submit"
                disabled={submitState.status === "submitting"}
                className="ac-btn-primary"
              >
                {submitState.status === "submitting" ? "Submitting..." : "Submit application"}
              </button>
            )}
          </div>
        </form>
        <Footer />
      </Shell>
    </FormProvider>
  );
}

function AboutStep() {
  const { register, control, formState } = useFormContext<FormValues>();
  const e = formState.errors;
  return (
    <section className="space-y-6">
      <SectionHeading title="About you" sub="Tell us how to reach you." />

      <Field label="Full name" error={e.fullName?.message} htmlFor="fullName">
        <input id="fullName" className="ac-field" {...register("fullName")} autoComplete="name" />
      </Field>

      <Field label="Gender" error={e.gender?.message}>
        <Controller
          control={control}
          name="gender"
          render={({ field }) => (
            <ChipGroup
              options={GENDER_OPTIONS}
              value={field.value}
              onChange={(v) => field.onChange(v)}
            />
          )}
        />
      </Field>

      <Field label="Country" error={e.country?.message} htmlFor="country">
        <select id="country" className="ac-field" defaultValue="" {...register("country")}>
          <option value="" disabled>
            Select your country
          </option>
          {COUNTRIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Email" error={e.email?.message} htmlFor="email">
        <input
          id="email"
          type="email"
          className="ac-field"
          placeholder="you@example.com"
          {...register("email")}
          autoComplete="email"
        />
      </Field>

      <Field
        label="WhatsApp number"
        sub="Include the country code, e.g. +44 7..."
        error={e.whatsapp?.message}
        htmlFor="whatsapp"
      >
        <input
          id="whatsapp"
          className="ac-field"
          placeholder="+1 555 123 4567"
          {...register("whatsapp")}
          autoComplete="tel"
        />
      </Field>

      <Field
        label="LinkedIn profile URL"
        error={e.linkedin?.message}
        htmlFor="linkedin"
      >
        <input
          id="linkedin"
          className="ac-field"
          placeholder="https://www.linkedin.com/in/your-handle"
          {...register("linkedin")}
        />
      </Field>
    </section>
  );
}

function ExperienceStep() {
  const { register, control, watch, formState } = useFormContext<FormValues>();
  const e = formState.errors;
  const expTypes = watch("experienceTypes");
  const cLevel = watch("cLevelExperience");
  const needsExpDetails =
    Array.isArray(expTypes) && expTypes.some((t) => t !== "I'm new to this");

  return (
    <section className="space-y-6">
      <SectionHeading
        title="Your experience"
        sub="A short snapshot of where you have worked and how senior you are."
      />

      <Field
        label="How would you rate your experience in the tech industry?"
        sub="From 1 (lowest) to 5 (highest)."
        error={(e.ratingTechIndustry as { message?: string })?.message}
      >
        <Controller
          control={control}
          name="ratingTechIndustry"
          render={({ field }) => (
            <StarRating value={field.value} onChange={(n) => field.onChange(n)} />
          )}
        />
      </Field>

      <Field
        label="How would you rate your knowledge of the Palestinian tech ecosystem?"
        sub="From 1 (lowest) to 5 (highest)."
        error={(e.ratingPalestinianEcosystem as { message?: string })?.message}
      >
        <Controller
          control={control}
          name="ratingPalestinianEcosystem"
          render={({ field }) => (
            <StarRating value={field.value} onChange={(n) => field.onChange(n)} />
          )}
        />
      </Field>

      <Field
        label="Which of the following do you have experience in?"
        sub="Select all that apply."
        error={(e.experienceTypes as { message?: string })?.message}
      >
        <Controller
          control={control}
          name="experienceTypes"
          render={({ field }) => (
            <ChipGroup
              multi
              options={EXPERIENCE_TYPES}
              value={field.value}
              onChange={(v) => field.onChange(v)}
            />
          )}
        />
      </Field>

      {needsExpDetails && (
        <Field
          label="Please share a few sentences of detail" optional
          error={e.experienceTypeDetails?.message}
          htmlFor="experienceTypeDetails"
        >
          <textarea
            id="experienceTypeDetails"
            rows={3}
            className="ac-field"
            placeholder="What kind of mentoring, coaching, advisory, or consulting have you done?"
            {...register("experienceTypeDetails")}
          />
        </Field>
      )}

      <Field
        label="Do you have experience working with C-level managers?"
        error={e.cLevelExperience?.message}
      >
        <Controller
          control={control}
          name="cLevelExperience"
          render={({ field }) => (
            <ChipGroup
              options={YES_NO}
              value={field.value}
              onChange={(v) => field.onChange(v)}
            />
          )}
        />
      </Field>

      {cLevel === "Yes" && (
        <Field
          label="Please share more detail" optional
          error={e.cLevelDetails?.message}
          htmlFor="cLevelDetails"
        >
          <textarea
            id="cLevelDetails"
            rows={3}
            className="ac-field"
            placeholder="Industries, seniority of executives, types of engagements..."
            {...register("cLevelDetails")}
          />
        </Field>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <Field
          label="Current position"
          error={e.currentPosition?.message}
          htmlFor="currentPosition"
        >
          <input
            id="currentPosition"
            className="ac-field"
            {...register("currentPosition")}
            autoComplete="organization-title"
          />
        </Field>
        <Field
          label="Current employer"
          error={e.currentEmployer?.message}
          htmlFor="currentEmployer"
        >
          <input
            id="currentEmployer"
            className="ac-field"
            {...register("currentEmployer")}
            autoComplete="organization"
          />
        </Field>
      </div>

      <Field
        label="Total years of experience"
        error={e.yearsOfExperience?.message}
      >
        <Controller
          control={control}
          name="yearsOfExperience"
          render={({ field }) => (
            <ChipGroup
              options={YEARS_OF_EXPERIENCE}
              value={field.value}
              onChange={(v) => field.onChange(v)}
            />
          )}
        />
      </Field>
    </section>
  );
}

function SupportStep() {
  const { register, control, watch, formState } = useFormContext<FormValues>();
  const e = formState.errors;
  const support = watch("supportArea") || [];
  const paid = watch("paidOrVolunteer");
  const markets = watch("markets") || [];

  return (
    <section className="space-y-6">
      <SectionHeading
        title="Where you can support"
        sub="What you would like to help with and where you have worked."
      />

      <Field
        label="I would like to support in"
        sub="Select all that apply."
        error={(e.supportArea as { message?: string })?.message}
      >
        <Controller
          control={control}
          name="supportArea"
          render={({ field }) => (
            <ChipGroup
              multi
              options={SUPPORT_AREA}
              value={field.value}
              onChange={(v) => field.onChange(v)}
            />
          )}
        />
      </Field>

      {support.includes("Non-Technical") && (
        <>
          <Field
            label="Non-Technical subjects you have experience in" optional
            sub="Select all that apply."
            error={(e.nonTechSubjects as { message?: string })?.message}
          >
            <Controller
              control={control}
              name="nonTechSubjects"
              render={({ field }) => (
                <ChipGroup
                  multi
                  options={NON_TECHNICAL_SUBJECTS}
                  value={field.value as string[]}
                  onChange={(v) => field.onChange(v)}
                />
              )}
            />
          </Field>
          <Field
            label="Other non-technical subjects" optional
            sub="Comma-separated, if not in the list above."
            error={e.nonTechSubjectsOther?.message}
            htmlFor="nonTechSubjectsOther"
          >
            <input
              id="nonTechSubjectsOther"
              className="ac-field"
              placeholder="e.g. Procurement, ESG, Localization"
              {...register("nonTechSubjectsOther")}
            />
          </Field>
        </>
      )}

      {support.includes("Technical") && (
        <>
          <Field
            label="Technical specializations or technologies" optional
            sub="Select all that apply."
            error={(e.techSpecializations as { message?: string })?.message}
          >
            <Controller
              control={control}
              name="techSpecializations"
              render={({ field }) => (
                <ChipGroup
                  multi
                  options={TECHNICAL_SPECIALIZATIONS}
                  value={field.value as string[]}
                  onChange={(v) => field.onChange(v)}
                />
              )}
            />
          </Field>
          <Field
            label="Other technical areas" optional
            error={e.techSpecializationsOther?.message}
            htmlFor="techSpecializationsOther"
          >
            <input
              id="techSpecializationsOther"
              className="ac-field"
              placeholder="e.g. Rust, Unity, Bioinformatics"
              {...register("techSpecializationsOther")}
            />
          </Field>
        </>
      )}

      <Field
        label="Have you worked or volunteered with GSG before?"
        error={e.workedWithGsgBefore?.message}
      >
        <Controller
          control={control}
          name="workedWithGsgBefore"
          render={({ field }) => (
            <ChipGroup
              options={YES_NO}
              value={field.value}
              onChange={(v) => field.onChange(v)}
            />
          )}
        />
      </Field>

      <Field
        label="Are you looking for a paid or volunteering opportunity?"
        error={e.paidOrVolunteer?.message}
      >
        <Controller
          control={control}
          name="paidOrVolunteer"
          render={({ field }) => (
            <ChipGroup
              options={PAID_OR_VOLUNTEER}
              value={field.value}
              onChange={(v) => field.onChange(v)}
            />
          )}
        />
      </Field>

      {(paid === "Paid" || paid === "Open to both") && (
        <Field
          label="Expected hourly rate in USD"
          optional
          sub="A number or short note, e.g. 75 or 50-100."
          error={e.hourlyRateUsd?.message}
          htmlFor="hourlyRateUsd"
        >
          <input
            id="hourlyRateUsd"
            className="ac-field"
            placeholder="75"
            {...register("hourlyRateUsd")}
          />
        </Field>
      )}

      <Field
        label="Markets / regions you have professional experience working in" optional
        sub="Select all that apply."
        error={(e.markets as { message?: string })?.message}
      >
        <Controller
          control={control}
          name="markets"
          render={({ field }) => (
            <ChipGroup
              multi
              options={MARKETS}
              value={field.value}
              onChange={(v) => field.onChange(v)}
            />
          )}
        />
      </Field>

      {markets.length > 0 && (
        <Field
          label="Briefly describe your experience in the selected markets" optional
          error={e.marketsExperience?.message}
          htmlFor="marketsExperience"
        >
          <textarea
            id="marketsExperience"
            rows={3}
            className="ac-field"
            placeholder="Industries, role, length of time, notable outcomes..."
            {...register("marketsExperience")}
          />
        </Field>
      )}
    </section>
  );
}

function EngagementStep() {
  const { register, control, watch, formState } = useFormContext<FormValues>();
  const e = formState.errors;
  const heard = watch("howHeard");

  return (
    <section className="space-y-6">
      <SectionHeading
        title="How you would engage"
        sub="The shape of the support that fits your time and interest."
      />

      <Field
        label="Which of the following ways would you be interested in engaging with Palestinian talent?"
        sub="Select all that apply."
        error={(e.engagementWays as { message?: string })?.message}
      >
        <Controller
          control={control}
          name="engagementWays"
          render={({ field }) => (
            <ChipGroup
              multi
              options={ENGAGEMENT_WAYS}
              value={field.value}
              onChange={(v) => field.onChange(v)}
            />
          )}
        />
      </Field>

      <Field
        label="I am interested in supporting GSG through"
        sub="Select all that apply."
        error={(e.supportModes as { message?: string })?.message}
      >
        <Controller
          control={control}
          name="supportModes"
          render={({ field }) => (
            <ChipGroup
              multi
              options={SUPPORT_MODES}
              value={field.value}
              onChange={(v) => field.onChange(v)}
              columns={1}
            />
          )}
        />
      </Field>

      <Field
        label="How did you hear about us?"
        error={e.howHeard?.message}
        htmlFor="howHeard"
      >
        <select id="howHeard" className="ac-field" defaultValue="" {...register("howHeard")}>
          <option value="" disabled>
            Pick an option
          </option>
          {HEAR_ABOUT_US.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
      </Field>

      {heard && (
        <Field
          label="Add more detail" optional
          sub={
            heard === "From a GSG team member" || heard === "From a friend or colleague"
              ? "Who told you about us?"
              : "Anything that helps us understand the channel."
          }
          error={e.howHeardOther?.message}
          htmlFor="howHeardOther"
        >
          <input
            id="howHeardOther"
            className="ac-field"
            {...register("howHeardOther")}
          />
        </Field>
      )}
    </section>
  );
}

function MentorshipStep() {
  const { register, control, watch, formState } = useFormContext<FormValues>();
  const e = formState.errors;
  const isStudent = watch("isUniversityStudent");
  const guidance = watch("guidanceTypes") || [];

  return (
    <section className="space-y-6">
      <SectionHeading
        title="Your mentorship"
        sub="How you would like to mentor and the time you can commit."
      />

      <Field
        label="Are you currently a university student?"
        error={e.isUniversityStudent?.message}
      >
        <Controller
          control={control}
          name="isUniversityStudent"
          render={({ field }) => (
            <ChipGroup
              options={YES_NO}
              value={field.value}
              onChange={(v) => field.onChange(v)}
            />
          )}
        />
      </Field>

      {isStudent === "Yes" && (
        <>
          <Field
            label="What are you currently studying/what have you studied?"
            error={e.studying?.message}
            htmlFor="studying"
          >
            <input
              id="studying"
              className="ac-field"
              placeholder="e.g. Computer Science, Business Administration"
              {...register("studying")}
            />
          </Field>

          <Field label="What level are you studying?" error={e.studyLevel?.message}>
            <Controller
              control={control}
              name="studyLevel"
              render={({ field }) => (
                <ChipGroup
                  options={STUDY_LEVELS}
                  value={field.value}
                  onChange={(v) => field.onChange(v)}
                />
              )}
            />
          </Field>
        </>
      )}

      <Field
        label="How many hours per month can you dedicate to mentorship?"
        sub="Estimate."
        error={e.hoursPerMonth?.message}
      >
        <Controller
          control={control}
          name="hoursPerMonth"
          render={({ field }) => (
            <ChipGroup
              options={MENTOR_HOURS}
              value={field.value}
              onChange={(v) => field.onChange(v)}
            />
          )}
        />
      </Field>

      <Field
        label="Preferred frequency of meetings"
        error={e.meetingFrequency?.message}
      >
        <Controller
          control={control}
          name="meetingFrequency"
          render={({ field }) => (
            <ChipGroup
              options={MENTOR_FREQUENCY}
              value={field.value}
              onChange={(v) => field.onChange(v)}
            />
          )}
        />
      </Field>

      <Field label="Preferred mentorship model" error={e.mentorshipModel?.message}>
        <Controller
          control={control}
          name="mentorshipModel"
          render={({ field }) => (
            <ChipGroup
              options={MENTOR_MODELS}
              value={field.value}
              onChange={(v) => field.onChange(v)}
              columns={1}
            />
          )}
        />
      </Field>

      <Field
        label="Would you prefer mentoring one or multiple mentees?"
        error={e.menteePreference?.message}
      >
        <Controller
          control={control}
          name="menteePreference"
          render={({ field }) => (
            <ChipGroup
              options={MENTEE_PREFERENCES}
              value={field.value}
              onChange={(v) => field.onChange(v)}
              columns={1}
            />
          )}
        />
      </Field>

      <Field label="Have you been a mentor before?" error={e.mentoredBefore?.message}>
        <Controller
          control={control}
          name="mentoredBefore"
          render={({ field }) => (
            <ChipGroup
              options={YES_NO}
              value={field.value}
              onChange={(v) => field.onChange(v)}
            />
          )}
        />
      </Field>

      <Field
        label="What type of guidance are you most comfortable providing?"
        sub="Select all that apply."
        error={(e.guidanceTypes as { message?: string })?.message}
      >
        <Controller
          control={control}
          name="guidanceTypes"
          render={({ field }) => (
            <ChipGroup
              multi
              options={GUIDANCE_TYPES}
              value={field.value}
              onChange={(v) => field.onChange(v)}
              columns={1}
            />
          )}
        />
      </Field>

      {guidance.includes("Other") && (
        <Field
          label="Please describe the other type of guidance"
          error={e.guidanceOther?.message}
          htmlFor="guidanceOther"
        >
          <input
            id="guidanceOther"
            className="ac-field"
            {...register("guidanceOther")}
          />
        </Field>
      )}

      {guidance.includes("Technical & Sector Expertise") && (
        <Field
          label="If you are offering technical mentorship, please specify your area(s) of expertise."
          error={e.technicalExpertise?.message}
          htmlFor="technicalExpertise"
        >
          <textarea
            id="technicalExpertise"
            rows={3}
            className="ac-field"
            placeholder="e.g. Frontend development, data science, product design..."
            {...register("technicalExpertise")}
          />
        </Field>
      )}

      <Field
        label="Would you be open to participating in a feedback session at the end of the program to share your experience?"
        error={e.feedbackSession?.message}
      >
        <Controller
          control={control}
          name="feedbackSession"
          render={({ field }) => (
            <ChipGroup
              options={YES_NO}
              value={field.value}
              onChange={(v) => field.onChange(v)}
            />
          )}
        />
      </Field>
    </section>
  );
}

function UploadStep({ track }: { track: Track }) {
  const { control, formState } = useFormContext<FormValues>();
  const optional = track === "mentor";
  return (
    <section className="space-y-6">
      <SectionHeading
        title={optional ? "Upload your CV (optional)" : "Upload your CV"}
        sub={
          optional
            ? "A current CV in PDF or Word format, up to 4 MB. It helps us match you well, but you can submit without one. We use it only to evaluate fit and never share it externally without your permission."
            : "A current CV in PDF or Word format, up to 4 MB. We use it only to evaluate fit and never share it externally without your permission."
        }
      />
      <Controller
        control={control}
        name="cv"
        render={({ field }) => (
          <FileDrop
            value={field.value}
            onChange={(v) => field.onChange(v)}
            error={(formState.errors.cv as { message?: string })?.message}
          />
        )}
      />
    </section>
  );
}

function ReviewStep({
  track,
  steps,
  onJump,
}: {
  track: Track;
  steps: StepKey[];
  onJump: (key: StepKey) => void;
}) {
  const { register, control, watch, formState } = useFormContext<FormValues>();
  const e = formState.errors;
  const v = watch();
  const heard = watch("howHeard");

  function fmt(value: unknown): string {
    if (value === undefined || value === null) return "";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "object" && value && "name" in (value as object))
      return String((value as { name: string }).name);
    return String(value);
  }

  const groups: { step: StepKey; title: string; rows: [string, string][] }[] = [
    {
      step: "about",
      title: "About you",
      rows: [
        ["Full name", fmt(v.fullName)],
        ["Gender", fmt(v.gender)],
        ["Country", fmt(v.country)],
        ["Email", fmt(v.email)],
        ["WhatsApp", fmt(v.whatsapp)],
        ["LinkedIn", fmt(v.linkedin)],
      ],
    },
    {
      step: "experience",
      title: "Experience",
      rows: [
        ["Tech industry rating", v.ratingTechIndustry ? `${v.ratingTechIndustry} / 5` : ""],
        [
          "Ecosystem knowledge rating",
          v.ratingPalestinianEcosystem ? `${v.ratingPalestinianEcosystem} / 5` : "",
        ],
        ["Experience in", fmt(v.experienceTypes)],
        ["C-level experience", fmt(v.cLevelExperience)],
        ["Position", fmt(v.currentPosition)],
        ["Employer", fmt(v.currentEmployer)],
        ["Years of experience", fmt(v.yearsOfExperience)],
      ],
    },
    {
      step: "support",
      title: "Support",
      rows: [
        ["Support area", fmt(v.supportArea)],
        ["Non-technical subjects", fmt(v.nonTechSubjects)],
        ["Technical specializations", fmt(v.techSpecializations)],
        ["Worked with GSG before", fmt(v.workedWithGsgBefore)],
        ["Paid or volunteering", fmt(v.paidOrVolunteer)],
        ["Hourly rate (USD)", fmt(v.hourlyRateUsd)],
        ["Markets", fmt(v.markets)],
      ],
    },
    {
      step: "engagement",
      title: "Engagement",
      rows: [
        ["Engagement ways", fmt(v.engagementWays)],
        ["Support modes", fmt(v.supportModes)],
        ["How you heard about us", fmt(v.howHeard)],
      ],
    },
    {
      step: "mentorship",
      title: "Mentorship",
      rows: [
        ["University student", fmt(v.isUniversityStudent)],
        ["Studying", fmt(v.studying)],
        ["Study level", fmt(v.studyLevel)],
        ["Hours per month", fmt(v.hoursPerMonth)],
        ["Meeting frequency", fmt(v.meetingFrequency)],
        ["Mentorship model", fmt(v.mentorshipModel)],
        ["Mentee preference", fmt(v.menteePreference)],
        ["Mentored before", fmt(v.mentoredBefore)],
        ["Guidance types", fmt(v.guidanceTypes)],
        ["Technical areas", fmt(v.technicalExpertise)],
        ["Feedback session", fmt(v.feedbackSession)],
      ],
    },
    {
      step: "upload",
      title: "CV",
      rows: [["File", v.cv ? fmt(v.cv) : track === "mentor" ? "Not provided (optional)" : ""]],
    },
  ];
  const visible = groups.filter((g) => steps.includes(g.step));

  return (
    <section className="space-y-6">
      <SectionHeading
        title="Review and submit"
        sub="Check your answers below. You can jump back to any step to edit."
      />

      <div className="space-y-4">
        {visible.map((g) => (
          <div key={g.step} className="rounded-xl2 border border-brand-line bg-brand-bg/60 p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[12px] uppercase tracking-[0.16em] font-bold text-brand-teal">
                {g.title}
              </div>
              <button
                type="button"
                onClick={() => onJump(g.step)}
                className="text-[12.5px] font-semibold text-brand-teal underline"
              >
                Edit
              </button>
            </div>
            <dl className="mt-2 space-y-1">
              {g.rows
                .filter(([, val]) => val !== "")
                .map(([label, val]) => (
                  <div key={label} className="flex flex-col sm:flex-row sm:items-baseline gap-x-3">
                    <dt className="text-[13px] text-brand-mute sm:w-56 shrink-0">{label}</dt>
                    <dd className="text-[14px] text-brand-ink font-medium break-words">
                      {val}
                    </dd>
                  </div>
                ))}
            </dl>
          </div>
        ))}
      </div>

      {track === "mentor" && (
        <>
          <Field
            label="How did you hear about us?"
            error={e.howHeard?.message}
            htmlFor="howHeard"
          >
            <select id="howHeard" className="ac-field" defaultValue="" {...register("howHeard")}>
              <option value="" disabled>
                Pick an option
              </option>
              {HEAR_ABOUT_US.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
          </Field>
          {heard && (
            <Field
              label="Add more detail" optional
              sub={
                heard === "From a GSG team member" || heard === "From a friend or colleague"
                  ? "Who told you about us?"
                  : "Anything that helps us understand the channel."
              }
              error={e.howHeardOther?.message}
              htmlFor="howHeardOther"
            >
              <input id="howHeardOther" className="ac-field" {...register("howHeardOther")} />
            </Field>
          )}
        </>
      )}

      <Field
        label={track === "mentor" ? "Comments" : "Anything else you would like to share"}
        optional
        error={e.anythingElse?.message}
        htmlFor="anythingElse"
      >
        <textarea
          id="anythingElse"
          rows={4}
          className="ac-field"
          placeholder="Time zone, availability, anything else you would like to share..."
          {...register("anythingElse")}
        />
      </Field>

      <Field
        label="Would you like to sign up for our monthly international newsletter?"
        error={e.newsletterOptIn?.message}
      >
        <Controller
          control={control}
          name="newsletterOptIn"
          render={({ field }) => (
            <ChipGroup
              options={YES_NO}
              value={field.value}
              onChange={(v2) => field.onChange(v2)}
            />
          )}
        />
      </Field>
    </section>
  );
}
